/**
 * Recently resolved issues — the "what just went wrong and is now fixed" feed.
 *
 * It matters on this board because a service can read as operational again
 * minutes after an outage users are still complaining about; this panel is the
 * evidence for that conversation.
 */

import { CircleCheck, Icon } from "@norican/icons";
import { Badge, Card, EmptyState, Timeline, Typography } from "@norican/react";

import type { M365Incident } from "@/lib/api";

import { formatDateTime, formatDuration } from "./status";

interface ResolvedPanelProps {
  incidents: M365Incident[];
  windowDays: number;
}

export function ResolvedPanel({ incidents, windowDays }: ResolvedPanelProps) {
  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Recently resolved
          </Typography>
          <Typography variant="body-sm" tone="muted">
            Last {windowDays} days
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        {incidents.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing resolved recently"
            description={`Microsoft has closed no issues for this tenant in the last ${windowDays} days.`}
          />
        ) : (
          <Timeline
            orientation="vertical"
            items={incidents.map((incident) => ({
              id: incident.id,
              tone: "success" as const,
              icon: <Icon icon={CircleCheck} size="sm" />,
              timestamp: formatDateTime(incident.end_date_time),
              title: (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-fg font-medium">{incident.title}</span>
                  <Badge tone="neutral" variant="outline" size="xs">
                    {incident.id}
                  </Badge>
                </span>
              ),
              description: (
                <span className="text-fg-muted text-xs">
                  {incident.service} · {incident.classification} · lasted{" "}
                  {formatDuration(incident.start_date_time, incident.end_date_time)}
                </span>
              ),
            }))}
          />
        )}
      </Card.Body>
    </Card>
  );
}
